import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useFormBuilder } from '../utils/useFormBuilder';
import AsyncSelect from 'react-select/async';
import {
  getHierarchyStructures,
  createHierarchyStructure,
  updateHierarchyStructure,
  deleteHierarchyStructure,
} from '../api/hierarchy-structureAPI';
import { getUniqueSourceOfSupply } from '../api/partsAPI';
import {
  createDepartmentSupplier,
  getDepartmentSuppliers,
  updateDepartmentSupplier,
} from '../api/department-suppliersAPI';
import { useSettings } from '../utils/AppSettingsContext';
import { loadSupplierOptions } from '../utils/supplierOptionUtils';
import { renderErrorDot } from '../utils/useValidator';

const HierarchyStructureListLayer = () => {
  const userProfile = useUserProfile();
  const [structureList, setStructureList] = useState([]);
  const [newStructure, setNewStructure] = useState({ isDepartment: false });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [errors, setErrors] = useState({});
  const [sourceOfSupplyList, setSourceOfSupplyList] = useState([]);
  const [supplierSelections, setSupplierSelections] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierSearchState, setSupplierSearchState] = useState({
    term: '',
    page: 1,
    hasMore: false,
  });
  const [departmentSupplierRecords, setDepartmentSupplierRecords] = useState([]);
  const showFunctionalLocation = useSettings('use_functional_location');
  const supplierRequestRef = useRef(0);

  const isDepartment = Boolean(newStructure.isDepartment);

  const fetchStructures = async () => {
    try {
      const data = await getHierarchyStructures();
      setStructureList(data);
    } catch (err) {
      console.error('Error fetching site or department:', err);
    }
  };

  const mergeSupplierOptions = useCallback((existing = [], incoming = []) => {
    const safeExisting = Array.isArray(existing) ? existing : [];
    const safeIncoming = Array.isArray(incoming) ? incoming : [];
    if (!safeExisting.length) return [...safeIncoming];
    if (!safeIncoming.length) return [...safeExisting];

    const result = [];
    const seen = new Map();

    const pushOption = (option) => {
      if (!option) return;
      const key = option.value !== undefined && option.value !== null ? option.value : null;
      if (key !== null) {
        if (seen.has(key)) {
          result[seen.get(key)] = option;
          return;
        }
        seen.set(key, result.length);
      }
      result.push(option);
    };

    safeExisting.forEach(pushOption);
    safeIncoming.forEach(pushOption);
    return result;
  }, []);

  const fetchSupplierOptions = useCallback(
    async (searchTerm = '', page = 1) => {
      const requestId = ++supplierRequestRef.current;
      setSupplierLoading(true);
      try {
        const result = await loadSupplierOptions(searchTerm, { page });
        if (requestId !== supplierRequestRef.current) {
          return Array.isArray(result) ? result : result?.options ?? [];
        }
        const options = Array.isArray(result) ? result : result?.options ?? [];
        setSupplierOptions((prev) =>
          page === 1 ? options : mergeSupplierOptions(prev, options),
        );
        setSupplierSearchState({
          term: searchTerm,
          page,
          hasMore: result?.hasMore ?? false,
        });
        return options;
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        return [];
      } finally {
        if (requestId === supplierRequestRef.current) {
          setSupplierLoading(false);
        }
      }
    },
    [mergeSupplierOptions],
  );

  const handleAsyncSupplierSearch = useCallback(
    (inputValue) => {
      const term = typeof inputValue === 'string' ? inputValue.trim() : '';
      return fetchSupplierOptions(term, 1);
    },
    [fetchSupplierOptions],
  );

  const handleSupplierMenuScroll = useCallback(() => {
    if (supplierLoading || !supplierSearchState.hasMore) return;
    const nextPage = (supplierSearchState.page || 1) + 1;
    fetchSupplierOptions(supplierSearchState.term, nextPage);
  }, [fetchSupplierOptions, supplierLoading, supplierSearchState]);

  const validateForm = () => {
    const newErrors = {};
    if (!newStructure.name?.trim()) newErrors.name = 'Name is required';
    if (newStructure.isDepartment && !newStructure.parentId) newErrors.parentId = 'Site is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchStructures();
  }, []);

  useEffect(() => {
    if (!isDepartment) {
      setNewStructure((prev) => ({ ...prev, parentId: null }));
    }
  }, [isDepartment]);

  useEffect(() => {
    if (!showModal) return;
    let isMounted = true;

    const loadSuppliersTab = async () => {
      try {
        const [sourceOfSupplyResponse, departmentSuppliersResponse] = await Promise.all([
          getUniqueSourceOfSupply(),
          editingId ? getDepartmentSuppliers() : Promise.resolve([]),
        ]);
        if (!isMounted) return;

        const sourceItems = Array.isArray(sourceOfSupplyResponse?.data)
          ? sourceOfSupplyResponse.data
          : Array.isArray(sourceOfSupplyResponse)
            ? sourceOfSupplyResponse
            : [];
        setSourceOfSupplyList(sourceItems);

        if (editingId) {
          const departmentSuppliers = Array.isArray(departmentSuppliersResponse?.data)
            ? departmentSuppliersResponse.data
            : Array.isArray(departmentSuppliersResponse)
              ? departmentSuppliersResponse
              : [];
          const departmentRecords = departmentSuppliers.filter(
            (item) => Number(item.departmentId) === Number(editingId),
          );
          const selections = {};
          departmentRecords.forEach((item) => {
            const supplier = item.supplier;
            if (!item.sourceOfSupplyId || !supplier?.id) return;
            selections[item.sourceOfSupplyId] = {
              value: supplier.id,
              label: supplier.name,
              raw: supplier,
            };
          });
          setSupplierSelections(selections);
          setDepartmentSupplierRecords(departmentRecords);
        } else {
          setSupplierSelections({});
          setDepartmentSupplierRecords([]);
        }
      } catch (error) {
        console.error('Error loading supplier data:', error);
      }
    };

    loadSuppliersTab();
    fetchSupplierOptions('', 1);

    return () => {
      isMounted = false;
    };
  }, [editingId, fetchSupplierOptions, showModal]);


  const formatDelivery = (s) => {
    const lines = [
      [s.deliveryAddressL1, s.deliveryAddressL2]
        .filter(Boolean)
        .join(', '),
      [s.deliveryCity, s.deliveryState, s.deliveryPostCode, s.deliveryCountry, s.deliveryPhone]
        .filter(Boolean)
        .join(' '),

    ].filter(Boolean);
    return lines;
  };

  const inputFields = useMemo(() => {
    const fields = [
      { field: 'name', label: 'Name', type: 'text', size: 12, required: true },
      { field: 'code', label: 'Code', type: 'text', size: 6, required: false },
      { field: 'functionalLocation', label: 'Functional Location', type: 'text', size: 6, required: false },
      { field: 'deliveryAddressL1', label: 'Delivery Address', type: 'text', size: 12 },
      { field: 'deliveryAddressL2', label: '', type: 'text', size: 12 },
      { field: 'deliveryCity', label: 'City', type: 'text', size: 6 },
      { field: 'deliveryState', label: 'State', type: 'text', size: 3 },
      { field: 'deliveryPostCode', label: 'Postcode', type: 'text', size: 3 },
      { field: 'deliveryCountry', label: 'Country', type: 'text', size: 12 },
      { field: 'deliveryPhone', label: 'Phone', type: 'text', size: 12 },
      { field: 'shiftLogFlag', label: 'Available for Shift Log?', type: 'switch', size: 12 },
      { field: 'allowEventOverlap', label: 'Allow Asset Events to Overlap?', type: 'switch', size: 12 },
      { field: 'isDepartment', label: 'Link to a Parent department?', type: 'switch', size: 12 },
    ];
    if (isDepartment) {
      let options = structureList;
      if (editingId) {
        options = options.filter(
          (s) => s.id !== editingId && s.parentId !== editingId
        );
      }
      fields.push({
        field: 'parentId',
        label: 'Select Parent Site or Department',
        type: 'select',
        size: 12,
        required: true,
        options: options.map((s) => ({
          value: s.id,
          label: s.name,
          parentName: structureList.find((p) => p.id === s.parentId)?.name,
        })),
        formatOptionLabel: (opt) => (
          <div className="d-flex align-items-center gap-2">
            <span>{opt.label}</span>
            {opt.parentName && (
              <span className="badge bg-primary">{opt.parentName}</span>
            )}
          </div>
        ),
      });
    }
    return fields;
  }, [isDepartment, structureList, editingId]);

  const { renderField } = useFormBuilder(inputFields, newStructure, setNewStructure, errors);
  const generalTabHasError = Object.keys(errors).length > 0;

  const handleSupplierSelection = useCallback((sourceOfSupplyId, option) => {
    setSupplierSelections((prev) => ({
      ...prev,
      [sourceOfSupplyId]: option || null,
    }));
  }, []);

  const handleEdit = (item) => {
    setNewStructure({ ...item, isDepartment: item.parentId !== null });
    setEditingId(item.id);
    setActiveTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      await deleteHierarchyStructure(id);
      await fetchStructures();
    } catch (error) {
      console.error('Error deleting site or department:', error);
    }
  };

  const searchTermLower = searchTerm.trim().toLowerCase();

  const visibleIds = useMemo(() => {
    if (!searchTermLower) {
      return new Set(structureList.map((item) => item.id));
    }

    const matches = new Set();

    const matchesSearch = (item) => {
      const parentName = structureList.find((p) => p.id === item.parentId)?.name;
      const delivery = formatDelivery(item).join(' ');
      const text = [item.name, parentName, delivery].filter(Boolean).join(' ').toLowerCase();
      return text.includes(searchTermLower);
    };

    structureList.forEach((item) => {
      if (matchesSearch(item)) {
        matches.add(item.id);
      }
    });

    return matches;
  }, [structureList, searchTermLower]);

  const hasVisibleRows = useMemo(() => structureList.some((item) => visibleIds.has(item.id)), [structureList, visibleIds]);

  const renderRows = (item, level) => {
    const children = structureList.filter((s) => s.parentId === item.id);
    const parentName = structureList.find((p) => p.id === item.parentId)?.name;
    const isVisible = visibleIds.has(item.id);
    return (
      <React.Fragment key={item.id}>
        {isVisible && (
          <tr>
            <td>
              <div
                className="d-flex justify-content-start align-items-center gap-2"
                style={{ marginLeft: `${level * 32}px` }}
              >
                <span className={`badge ${level === 0 ? 'bg-secondary' : 'bg-neutral-400'} ms-2`}>
                  <Icon icon={level === 0 ? 'mdi:factory' : 'mdi:store'} className="icon text-xl line-height-1" />
                </span>
                <span>{item.name}</span>
                {parentName && (
                  <span className="badge bg-primary ms-2">{parentName}</span>
                )}
              </div>
            </td>
            <td>
              {item.code}
            </td>
            {showFunctionalLocation && (
              <td>
                {item.functionalLocation}
              </td>
            )}
            <td>
              <div className="form-switch switch-primary d-inline-flex align-items-center gap-2 mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={`allow-event-overlap-${item.id}`}
                  checked={Boolean(item.allowEventOverlap)}
                  disabled
                  readOnly
                />
                <label className="form-check-label" htmlFor={`allow-event-overlap-${item.id}`}>
                  {item.allowEventOverlap ? 'Yes' : 'No'}
                </label>
              </div>
            </td>
            <td>
              <div className="form-switch switch-primary d-inline-flex align-items-center gap-2 mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={`shift-log-flag-${item.id}`}
                  checked={Boolean(item.shiftLogFlag)}
                  disabled
                  readOnly
                />
                <label className="form-check-label" htmlFor={`shift-log-flag-${item.id}`}>
                  {item.shiftLogFlag ? 'Yes' : 'No'}
                </label>
              </div>
            </td>
            <td>
              <div className="d-flex gap-2">
                {userProfile?.privileges?.includes('hierarchy-structures:update') && (
                  <button
                    className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    onClick={() => handleEdit(item)}
                  >
                    <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                  </button>
                )}
                {userProfile?.privileges?.includes('hierarchy-structures:delete') && (
                  <button
                    className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                  </button>
                )}
              </div>
            </td>
          </tr>
        )}
        {children.map((child) => renderRows(child, level + 1))}
      </React.Fragment>
    );
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {};
      for (const key in newStructure) {
        if (newStructure[key] !== null && typeof newStructure[key] !== 'object') {
          payload[key] = newStructure[key];
        }
      }

      payload.parentId = newStructure.isDepartment ? newStructure.parentId : null;

      const selectedSuppliers = Object.entries(supplierSelections)
        .map(([sourceOfSupplyId, option]) => ({
          sourceOfSupplyId: Number(sourceOfSupplyId),
          supplierId: option?.value ? Number(option.value) : null,
        }))
        .filter((item) => item.sourceOfSupplyId);

      let savedRecordId = editingId;
      if (editingId) {
        await updateHierarchyStructure(editingId, payload);
      } else {
        const created = await createHierarchyStructure(payload);
        savedRecordId = created?.id ?? created?.data?.id ?? null;
      }

      if (savedRecordId && selectedSuppliers.length > 0) {
        const departmentSupplierBySource = new Map(
          departmentSupplierRecords
            .map((record) => ({
              ...record,
              sourceOfSupplyId: Number(record.sourceOfSupplyId ?? record.sourceOfSupply?.id),
            }))
            .filter((record) => record.sourceOfSupplyId)
            .map((record) => [record.sourceOfSupplyId, record]),
        );

        const supplierTasks = selectedSuppliers
          .map((entry) => {
            const existing = departmentSupplierBySource.get(entry.sourceOfSupplyId);
            if (existing?.id) {
              return updateDepartmentSupplier(existing.id, {
                departmentId: savedRecordId,
                sourceOfSupplyId: entry.sourceOfSupplyId,
                supplierId: entry.supplierId ?? null,
              });
            }
            if (!entry.supplierId) return null;
            return createDepartmentSupplier({
              departmentId: savedRecordId,
              sourceOfSupplyId: entry.sourceOfSupplyId,
              supplierId: entry.supplierId,
            });
          })
          .filter(Boolean);

        if (supplierTasks.length > 0) {
          await Promise.all(supplierTasks);
        }
      }

      setShowModal(false);
      setEditingId(null);

      setNewStructure({ isDepartment: false });
      await fetchStructures();
    } catch (error) {
      console.error('Error saving site or department:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0 d-flex flex-column">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('hierarchy-structures:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewStructure({ isDepartment: false });
              setEditingId(null);
              setActiveTab('general');
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Site or Department
          </button>
        )}
      </div>

      <div className="card-body p-0 d-flex flex-column overflow-hidden">
        <div
          className="table-responsive scroll-sm flex-grow-1"
          style={{ height: 'calc(100vh - 150px)', overflowY: 'auto' }}
        >
          <table className="table bordered-table mb-3 w-100 rounded-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                {showFunctionalLocation && (
                  <th>
                    Functional Location
                  </th>
                )}
                <th>Allow Event Overlap?</th>
                <th>Shift Log?</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {structureList
                .filter((s) => !s.parentId)
                .map((parent) => renderRows(parent, 0))}
              {structureList.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-2">
                    No Sites & Departments items found
                  </td>
                </tr>
              )}
              {structureList.length > 0 && !hasVisibleRows && (
                <tr>
                  <td colSpan={3} className="text-center py-2">
                    No results match your search
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? `Edit ${isDepartment ? 'Department' : 'Site'}` : `Add ${isDepartment ? 'Department' : 'Site'}`}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                      type="button"
                      role="tab"
                      onClick={() => setActiveTab('general')}
                      aria-selected={activeTab === 'general'}
                    >
                      General{renderErrorDot(generalTabHasError && activeTab !== 'general')}
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'suppliers' ? 'active' : ''}`}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === 'suppliers'}
                      onClick={() => setActiveTab('suppliers')}
                    >
                      Parts Suppliers
                    </button>
                  </li>
                </ul>

                <div className="tab-content pt-3">
                  <div
                    className={`tab-pane fade ${activeTab === 'general' ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    <div className="row g-3">{inputFields.map(renderField)}</div>
                  </div>
                  <div
                    className={`tab-pane fade ${activeTab === 'suppliers' ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    {sourceOfSupplyList.length === 0 ? (
                      <div className="text-muted">No source of supply records found.</div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex flex-column" style={{ width: "250px" }}>
                            <span className="fw-semibold">Source Of Supply</span>
                          </div>
                          <div style={{ minWidth: '260px', flex: 1 }}>
                            <span>
                              <span className="fw-semibold">Default Local Supplier</span>
                            </span>
                          </div>

                        </div>

                        {sourceOfSupplyList.map((item) => {
                          const sourceOfSupplyId =
                            item?.sourceOfSupplyId ?? item?.sourceOfSupply?.id ?? null;
                          if (!sourceOfSupplyId) return null;
                          const manufacturerName =
                            item?.sourceOfSupply?.manufacturer ??
                            item?.manufacturer ??
                            item?.sourceOfSupply?.name ??
                            '';
                          const selectedSupplier = supplierSelections[sourceOfSupplyId] || null;

                          return (
                            <div
                              key={sourceOfSupplyId}
                              className="d-flex align-items-center justify-content-between gap-3"
                            >
                              <div className="d-flex flex-column" style={{ width: "250px" }}>
                                <span>{manufacturerName || 'Unknown Manufacturer'}</span>
                              </div>
                              <div style={{ minWidth: '260px', flex: 1 }}>
                                <AsyncSelect
                                  classNamePrefix="react-select"
                                  loadOptions={handleAsyncSupplierSearch}
                                  defaultOptions={supplierOptions}
                                  cacheOptions
                                  value={selectedSupplier}
                                  onChange={(option) =>
                                    handleSupplierSelection(sourceOfSupplyId, option)
                                  }
                                  placeholder="Select supplier"
                                  menuPortalTarget={document.body}
                                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                  onMenuScrollToBottom={handleSupplierMenuScroll}
                                  isLoading={supplierLoading}
                                  isClearable
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyStructureListLayer;
